import type { AllFilterStates } from '../../../common/constants/filters/types';
import type {
	ThirdPartyConfigsBootstrap,
	ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import {
	convertQueryParamsToStringArray,
	getDatasourceFromId,
	parseQueryParams,
} from '../../../common/utils/query-params';
import type { QueryParams } from '../../../common/utils/query-params/types';
import { allFilterData } from '../../../controllers/store/search/filters/all-filter-data';
import { FilterUtils } from '../../../controllers/store/search/filters/utils';

type GetSearchConfigArgs = {
	cloudId: string;
	queryParams: QueryParams;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
};

export const getSearchConfig = ({
	cloudId,
	queryParams,
	thirdPartyConfigs,
}: GetSearchConfigArgs): {
	cql: string | undefined;
	searchQuery: string;
	originalQuery: string | undefined;
	filters: Record<string, AllFilterStates | undefined>;
	selectedProducts: string[];
	selectedDatasource: ThirdPartyConfigsWithAPIResponse | undefined;
} => {
	// Parse query params so they're typed more strongly
	const parsedQueryParams = parseQueryParams(queryParams);
	const { lastModified_lt: _, lastModified_gt: __, ...parsedQueryParamsInUse } = parsedQueryParams;
	const queryParamsWithValuesAsStringArray = convertQueryParamsToStringArray(
		parsedQueryParamsInUse,
		Object.keys(parsedQueryParams),
	);

	// Get utilities for processing query params into filters
	const { getSelectedProducts } = FilterUtils(allFilterData, {});

	// Get selected products from query params
	const selectedProducts = getSelectedProducts(queryParams);

	// Get minimal filters object containing selected values based on query params
	const filters: Record<string, AllFilterStates | undefined> = Object.fromEntries(
		Object.entries(allFilterData).map(([filterId, filter]) => {
			return [
				filterId,
				filter.queryParams.getState(queryParamsWithValuesAsStringArray, cloudId) as AllFilterStates,
			];
		}),
	);

	// Get selected datasource
	const selectedDatasource = getDatasourceFromId(
		parsedQueryParams.datasource,
		selectedProducts[0],
		thirdPartyConfigs,
	);

	return {
		cql: parsedQueryParams.cql,
		searchQuery: parsedQueryParams.text || '',
		originalQuery: parsedQueryParams.originalQuery,
		filters,
		selectedProducts,
		selectedDatasource,
	};
};
